import moment, { Moment } from 'moment';
import { useState } from 'react';
import { Space, SpaceBooking, SpaceGroup } from '../../types';

import { InFormLogin } from 'components/Form/InFormLogin';
import { SubmitInput } from 'components/Form/SubmitInput';
import { Form } from 'components/Form/base/Form';
import Loader from 'components/Loader';
import { usePaymentHandler } from 'components/MollieConnect/hooks';
import { MappedSpace } from 'components/SpaceBooking/SpaceTable/types';
import { timeDivisions } from 'components/TrainingPlan/TrainingPlanHelpers';
import { Button } from 'components/styledComponents/Button';
import { useVirtualKeyboard } from 'hooks/useVirtualKeyboard';
import Joi from 'joi';
import { useSelector } from 'react-redux';
import { AppState } from 'redux/store';
import styled from 'styled-components';
import useSWR from 'swr';
import { StyledInput } from '~/components/ui';
import { BookingForm } from '.';
import { useApi } from '../../../../hooks/useApi';
import ClubMemberAuthorization, {
    useClubMemberAuthStore
} from '../../../ClubMemberManagement/ClubMember/ClubMemberAuthorization';
import FormFields from '../../../Formular/FormFields';
import SportisionAppBookingAd from '../../../SportisionAppBookingAd';
import VoucherFields from '../../../Vouchers/VoucherFields';
import { VoucherCardAssistent } from '../../VoucherCard/VoucherCardAssistent';
import { BookingConfirmationMessage } from '../BookingConfirmationMessage';

const StyledBookingData = styled.div`
    margin: 2rem 0;
    padding: 1rem 1.5rem;
    border-radius: ${({ theme }) => theme.borderRadius};
    box-shadow: ${({ theme }) => theme.shadows.md};

    > div {
        display: grid;
        grid-template-columns: repeat(2, 1fr);

        > .header {
            font-weight: bold;
        }
    }
`;

interface Props {
    space?: MappedSpace;
    spaceGroup: SpaceGroup;
    clubSlug?: string;
    startTime?: number;
    startDate?: Moment;
}

export const SpaceBookingAssistent = ({ space, spaceGroup, startTime, startDate, clubSlug }: Props) => {
    const api = useApi();
    const [bookingData, setBookingData] = useState<any>();
    const [booking, setBooking] = useState<SpaceBooking>(null);
    const [userEmail, setUserEmail] = useState<string>();
    const [isLoading, setIsLoading] = useState(false);
    const isAuthenticated = useSelector((state: AppState) => state.auth.isAuthenticated);
    const { data: formData, error: formError } = useSWR(spaceGroup?.form_id ? `form/${spaceGroup?.form_id}` : null);
    const fields = formData?.data?.fields;
    const { redirectToCheckout, preparePopup, handleError } = usePaymentHandler();
    const { clubMemberAuthToken } = useClubMemberAuthStore();
    useVirtualKeyboard(`input`);

    const handleSubmit = (data) => {
        setIsLoading(true);
        let popupWindow =
            !data.space_voucher_card_id && spaceGroup.allow_online_payments && bookingData?.calculatedPrice > 0
                ? preparePopup()
                : null;
        return api({
            url: `space/booking`,
            method: 'POST',
            data: {
                ...data,
                booking: { ...bookingData, spaces: bookingData.spaces.map((space: Space) => space.id) },
                clubMemberAuthToken
            },
            onBefore: () => setIsLoading(false),
            onError: () => handleError(popupWindow),
            onSuccess: (data) => {
                redirectToCheckout({
                    ...data,
                    popupWindow,
                    callback: () => {
                        setBooking(data.data || data);
                    }
                });
            }
        });
    };

    const schema = Joi.object({}).unknown();

    if (booking) {
        return <BookingConfirmationMessage booking={booking} />;
    }

    if (!bookingData)
        return (
            <>
                <SportisionAppBookingAd className="!tw-mt-0" small />
                {!isAuthenticated && (
                    <StyledInput
                        label="E-Mail Adresse"
                        name="email"
                        value={userEmail || ''}
                        onChange={(e) => setUserEmail(e.target.value)}
                    />
                )}
                <ClubMemberAuthorization email={userEmail} clubSlug={clubSlug} />
                <BookingForm
                    email={userEmail}
                    space={space}
                    spaceGroup={spaceGroup}
                    startTime={startTime}
                    startDate={startDate}
                    callback={(booking) => setBookingData(booking)}
                />
            </>
        );

    return (
        <>
            <Button block small variant="secondary" type="button" onClick={() => setBookingData(null)}>
                Buchungszeitraum bearbeiten
            </Button>
            <StyledBookingData>
                <h3>Buchungsdaten</h3>
                <div>
                    <span className="header">Datum</span>
                    <span>{moment(bookingData.start).format('LL')}</span>
                    <span className="header">Uhrzeit</span>
                    <span>
                        {timeDivisions.times[bookingData.start_time]} bis {timeDivisions.times[bookingData.end_time]}
                    </span>
                    <span className="header">Plätze</span>
                    <span>{bookingData.spaces.map((space) => space.name).join(', ')}</span>
                </div>
            </StyledBookingData>
            <Loader isLoading={spaceGroup?.form_id && !formData && !formError}>
                <Form onSubmit={handleSubmit} validationSchema={schema} defaultValues={{ email: userEmail }}>
                    {!isAuthenticated && <InFormLogin />}
                    {fields && (
                        <>
                            <hr className="my-4" />
                            <FormFields clubSlug={clubSlug} email={userEmail} fields={fields} namePrefix="form_data" />
                        </>
                    )}
                    <VoucherCardAssistent
                        email={userEmail}
                        clubSlug={clubSlug}
                        usedUnits={bookingData.end_time - bookingData.start_time}
                    />
                    <VoucherFields type="space_bookings" />
                    <SubmitInput block isLoading={isLoading}>
                        Termin verbindlich buchen
                    </SubmitInput>
                </Form>
            </Loader>
        </>
    );
};
